<template>
    <OrthographicWrapper :visible="visible">
        <BgSwirl ref="refBg" />
        <BgSpeedLines ref="refSpeedLines" />

        <TextRows ref="refTextRowsIntro" :highlightIndex="2" />
        <TextRows ref="refTextRowsReveal" :highlightIndex="-1" :highlightFg="false" />
        <TextFromCenter ref="refTextFromCenter" />
        <TextScaleIn ref="refTextScaleIn" />
    </OrthographicWrapper>
    <TextWrap ref="refTextWrap" />
</template>

<script setup>
    import { noop } from '@vueuse/core';
    import { ref } from 'vue';

    import { randomInt } from '@resn/gozer-math';
    import { useThreeObject } from '@resn/gozer-vue';
    import gsap from '@resn/gsap';

    import BgSpeedLines from './modules/BgSpeedLines.vue';
    import BgSwirl from './modules/BgSwirl.vue';
    import TextFromCenter from './modules/TextFromCenter.vue';
    import TextScaleIn from './modules/TextScaleIn.vue';
    import TextWrap from './modules/TextWrap.vue';
    import TextRows from '~/components/gl/Cards/reveals/chase/modules/TextRows.vue';
    import OrthographicWrapper from '~/components/gl/common/OrthographicWrapper';
    import { useAudio } from '~/providers/AudioProvider';

    const refTextRowsIntro = ref(null);
    const refTextRowsReveal = ref(null);
    const refTextFromCenter = ref(null);
    const refTextScaleIn = ref(null);
    const refTextWrap = ref(null);
    const refBg = ref(null);
    const refSpeedLines = ref(null);

    const visible = ref(false);

    const introComps = [refTextWrap, refTextScaleIn, refTextRowsIntro];
    const revealComps = [refTextFromCenter, refTextRowsReveal];
    const fastRevealComps = [refTextFromCenter, refTextRowsReveal];
    // const introComps = [refTextWrap];
    // const revealComps = [refTextRowsReveal];

    useThreeObject(null, { name: 'ChaseReveals', props: { v: visible } });

    // useThreeObject(null, { name: 'CardInfo' });
    const audio = useAudio();

    const show = ({ delay = 0, fast = false } = {}) => {
        const tl = new gsap.timeline({
            delay,
            onStart: () => {
                visible.value = true;

                audio.play('cardInfoReveal');
            },
            onComplete: () => {
                visible.value = false;
            },
        });

        const showSwirl = Math.random() > 0.5;
        const showSpeedLines = showSwirl ? false : Math.random() > 0.3;

        const intro = introComps[randomInt(0, introComps.length - 1)];
        const reveal = revealComps[randomInt(0, revealComps.length - 1)];

        if (fast) {
            const fastReveal = fastRevealComps[randomInt(0, fastRevealComps.length - 1)];

            tl.add(() => emit('revealComplete'), 0);
            tl.add(() => fastReveal.value.show(), 0.5);
            tl.add(noop, 2.5);
        } else {
            tl.addLabel('intro', 0);
            tl.addLabel('reveal', 1.3);

            tl.add(() => refBg.value.show({ swirl: showSwirl }), 'intro');
            tl.add(() => intro.value.show(), `intro+=${showSwirl ? 0.2 : 0}`);
            tl.add(() => emit('revealComplete'), 'reveal+=0.8');
            tl.add(() => reveal.value.show(), 'reveal');
            tl.add(() => refBg.value.hide({ swirl: showSwirl }), 'reveal+=0.8');
            if (showSpeedLines) {
                tl.add(() => refSpeedLines.value.show(), 'intro');
                tl.add(() => refSpeedLines.value.hide(), 'intro+=1');
                tl.add(() => refSpeedLines.value.show(), 'reveal+=0.8');
                tl.add(() => refSpeedLines.value.hide(), 'reveal+=1.8');
            }
            tl.add(noop, 'reveal+=3');
        }

        return tl;
    };

    const emit = defineEmits(['revealComplete']);

    defineExpose({ show });
</script>
